import { createSlice } from '@reduxjs/toolkit';

import { AuthorizedSignatorySchema } from 'services/authorizedSignatories';
import { searchPersonByEmail } from 'components/InvitePersons/thunks';
import { authorizeSignatory } from 'pages/Profile/thunks';
import {
  fetchSignatoryDetails,
  saveSignatoryDetails,
} from './components/signatoryDetails/thunks';

interface SubmitDashboardProps {
  isModalOpened: boolean;
  step: number;
  isDDAChecked: boolean;
  termsCheckbox: {
    show: boolean;
    checked: boolean;
  };
  privacyCheckbox: {
    show: boolean;
    checked: boolean;
  };
  authorizing: boolean;
  isNMIAccepted: boolean;
  isOatFiAccepted: boolean;
  showOatFiOnboardingModal: boolean;
  signatoryDetails: {
    isPopupOpen: boolean;
    loading: boolean;
    saving: boolean;
    data: AuthorizedSignatorySchema | null;
  };
}

export const initialState: SubmitDashboardProps = {
  isModalOpened: false,
  step: 1,
  isDDAChecked: false,
  termsCheckbox: {
    show: false,
    checked: false,
  },
  privacyCheckbox: {
    show: false,
    checked: false,
  },
  authorizing: false,
  isNMIAccepted: false,
  isOatFiAccepted: false,
  showOatFiOnboardingModal: false,
  signatoryDetails: {
    isPopupOpen: false,
    loading: false,
    saving: false,
    data: null,
  },
};

const submitDashboardSlice = createSlice({
  name: 'submitDashboard',
  initialState,
  reducers: {
    setStep(state, action) {
      state.step = action.payload;
    },
    setIsDDAChecked(state, action) {
      state.isDDAChecked = action.payload;
    },
    setTermsCheckbox(state, action) {
      state.termsCheckbox = {
        ...state.termsCheckbox,
        ...action.payload,
      };
    },
    setPrivacyCheckbox(state, action) {
      state.privacyCheckbox = {
        ...state.privacyCheckbox,
        ...action.payload,
      };
    },
    setIsModalOpened(state, action) {
      if (action.payload) {
        state.isModalOpened = action.payload;
      } else {
        Object.assign(state, initialState);
      }
    },
    setIsNMIAccepted(state, action) {
      state.isNMIAccepted = action.payload;
    },
    setIsOatFiAccepted(state, action) {
      state.isOatFiAccepted = action.payload;
    },
    setShowOatFiOnboardingModal(state, action) {
      state.showOatFiOnboardingModal = action.payload;
    },
    openSignatoryPopup(state) {
      state.signatoryDetails.isPopupOpen = true;
    },
    closeSignatoryPopup(state) {
      state.signatoryDetails.isPopupOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authorizeSignatory.pending, (state) => {
      state.authorizing = true;
    });
    builder.addCase(authorizeSignatory.rejected, (state) => {
      state.authorizing = false;
    });
    builder.addCase(authorizeSignatory.fulfilled, (state) => {
      state.authorizing = false;
    });
    builder.addCase(searchPersonByEmail.fulfilled, (state) => {
      state.isModalOpened = false;
    });
    builder.addCase(fetchSignatoryDetails.pending, (state) => {
      state.signatoryDetails.loading = true;
    });
    builder.addCase(fetchSignatoryDetails.fulfilled, (state, action) => {
      state.signatoryDetails.loading = false;
      state.signatoryDetails.data = action.payload || null;
    });
    builder.addCase(fetchSignatoryDetails.rejected, (state) => {
      // keep any previously fetched data - a transient fetch failure should
      // not blank the prefill
      state.signatoryDetails.loading = false;
    });
    builder.addCase(saveSignatoryDetails.pending, (state) => {
      state.signatoryDetails.saving = true;
    });
    builder.addCase(saveSignatoryDetails.fulfilled, (state, action) => {
      state.signatoryDetails.saving = false;
      state.signatoryDetails.isPopupOpen = false;
      state.signatoryDetails.data =
        action.payload || state.signatoryDetails.data;
    });
    builder.addCase(saveSignatoryDetails.rejected, (state, action: any) => {
      state.signatoryDetails.saving = false;

      // the details were stored but the attestation call failed - close the
      // popup and keep the freshly saved record so a retry reopens with the
      // values the user just submitted
      if (action.payload?.stage === 'attestation') {
        state.signatoryDetails.isPopupOpen = false;
        state.signatoryDetails.data =
          action.payload.signatory || state.signatoryDetails.data;
      }
    });
  },
});

export const {
  setStep,
  setIsDDAChecked,
  setTermsCheckbox,
  setPrivacyCheckbox,
  setIsModalOpened,
  setIsNMIAccepted,
  setIsOatFiAccepted,
  setShowOatFiOnboardingModal,
  openSignatoryPopup,
  closeSignatoryPopup,
} = submitDashboardSlice.actions;

export default submitDashboardSlice.reducer;
